
import { Link } from 'react-router-dom';
import { FileText, Linkedin, Mail } from 'lucide-react';
import { CookieSettingsLauncher } from "@/components/cookies/CookieSettingsLauncher";
import { cn } from '@/lib/utils';

interface FooterProps {
  noMarginTop?: boolean;
}

const Footer: React.FC<FooterProps> = ({ noMarginTop = false }) => {
  return (
    <footer className={cn(
      "relative bg-background py-12 shadow-[inset_0_1px_0_0_rgba(0,0,0,0.03),inset_0_8px_20px_-10px_rgba(0,0,0,0.06)]",
      noMarginTop ? "mt-0" : "mt-20"
    )}>
      {/* Subtle gradient line separator at top */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-border to-transparent" />
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-12">
          {/* Brand and description - 4 columns on medium screens and up */}
          <div className="md:col-span-4">
            <Link to="/" className="flex items-center mb-4 group">
              <FileText className="h-6 w-6 text-primary mr-2 transition-transform group-hover:scale-110" />
              <span className="font-semibold text-xl tracking-tight">PaperVerse</span>
            </Link>
            <p className="text-muted-foreground text-sm leading-relaxed mb-6 max-w-md">
              Write, review, and revise research papers with grounded AI assistance while keeping researchers in control.
            </p>
            <div className="flex space-x-5">
              <a 
                href="https://linkedin.com/company/paperverse" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="text-muted-foreground hover:text-primary transition-colors"
                aria-label="LinkedIn"
              >
                <Linkedin size={20} />
              </a>
              <a 
                href="mailto:contact@paperverse.io" 
                className="text-muted-foreground hover:text-primary transition-colors"
                aria-label="Email"
              >
                <Mail size={20} />
              </a>
            </div>
          </div>
          
          {/* Navigation columns - rebalanced widths */}
          <div className="md:col-span-3 md:ml-auto">
            <h4 className="font-medium mb-5 text-sm tracking-wider uppercase text-muted-foreground">Platform</h4>
            <ul className="space-y-3">
              <li><Link to="/" className="text-foreground/80 hover:text-primary transition-colors text-sm">Home</Link></li>
              <li><Link to="/write" className="text-foreground/80 hover:text-primary transition-colors text-sm">Write</Link></li>
              <li><Link to="/explore" className="text-foreground/80 hover:text-primary transition-colors text-sm">Explore</Link></li>
              <li><Link to="/pricing" className="text-foreground/80 hover:text-primary transition-colors text-sm">Pricing</Link></li>
              <li><Link to="/upload" className="text-foreground/80 hover:text-primary transition-colors text-sm">Upload</Link></li>
              <li><Link to="/about" className="text-foreground/80 hover:text-primary transition-colors text-sm">About</Link></li>
            </ul>
          </div>
          
          <div className="md:col-span-2">
            <h4 className="font-medium mb-5 text-sm tracking-wider uppercase text-muted-foreground">Resources</h4>
            <ul className="space-y-3">
              <li><Link to="/faq" className="text-foreground/80 hover:text-primary transition-colors text-sm">FAQ</Link></li>
              <li><Link to="/compare/paperverse-vs-overleaf" className="text-foreground/80 hover:text-primary transition-colors text-sm">Writer vs Overleaf</Link></li>
              <li><Link to="/guidelines" className="text-foreground/80 hover:text-primary transition-colors text-sm">Guidelines</Link></li>
              <li><Link to="/ai-transparency" className="text-foreground/80 hover:text-primary transition-colors text-sm">AI Transparency</Link></li>
            </ul>
          </div>
          
          <div className="md:col-span-3">
            <h4 className="font-medium mb-5 text-sm tracking-wider uppercase text-muted-foreground">Legal</h4>
            <ul className="space-y-3">
              <li><Link to="/terms-of-service" className="text-foreground/80 hover:text-primary transition-colors text-sm">Terms of Service</Link></li>
              <li><Link to="/privacy-policy" className="text-foreground/80 hover:text-primary transition-colors text-sm">Privacy Policy</Link></li>
              <li><Link to="/imprint" className="text-foreground/80 hover:text-primary transition-colors text-sm">Imprint</Link></li>
              <li><Link to="/cancel-contract" className="text-foreground/80 hover:text-primary transition-colors text-sm">Cancel contract here</Link></li>
              <li><Link to="/accessibility" className="text-foreground/80 hover:text-primary transition-colors text-sm">Accessibility</Link></li>
            </ul>
          </div>
        </div>
        
        {/* Sub-footer - single horizontal line */}
        <div className="pt-8 border-t border-border/30">
          <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-2 text-xs text-muted-foreground">
            <span>&copy; {new Date().getFullYear()} PaperVerse</span>
            <span className="text-muted-foreground/50" aria-hidden="true">·</span>
            <Link to="/cookie-policy" className="hover:text-primary transition-colors">Cookie Policy</Link>
            <span className="text-muted-foreground/50" aria-hidden="true">·</span>
            <Link to="/eu-processing" className="hover:text-primary transition-colors">EU Processing</Link>
            <span className="text-muted-foreground/50" aria-hidden="true">·</span>
            <Link to="/cancellation-policy" className="hover:text-primary transition-colors">Cancellation Policy</Link>
            <span className="text-muted-foreground/50" aria-hidden="true">·</span>
            <Link to="/refund-policy" className="hover:text-primary transition-colors">Refund Policy</Link>
            <span className="text-muted-foreground/50" aria-hidden="true">·</span>
            <Link to="/copyright" className="hover:text-primary transition-colors">Copyright</Link>
            <span className="text-muted-foreground/50" aria-hidden="true">·</span>
            <CookieSettingsLauncher />
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
